const normalizeRotation = (rotation) =>
  ((Number(rotation) % 360) + 360) % 360;

export const isPdfBytes = (bytes) =>
  bytes?.length > 4 &&
  bytes[0] === 0x25 &&
  bytes[1] === 0x50 &&
  bytes[2] === 0x44 &&
  bytes[3] === 0x46; // %PDF

export const sniffImageContentType = (bytes) => {
  if (!bytes || bytes.length < 12) return "";
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return "image/gif";
  }
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return "";
};

export const isImageBytes = (bytes) => Boolean(sniffImageContentType(bytes));

/**
 * OCR polygons are measured in page-image space, so a page image is the most
 * accurate and stable surface for hover markers. PDF.js is the fallback when
 * no usable page image exists.
 */
export const selectDocumentViewerSource = ({
  currentIndex = 1,
  imageList = [],
  failedImageUrl = "",
  documentId = "",
  pdfUrl = "",
  fileName = "",
} = {}) => {
  const requestedPage = Math.max(1, Number(currentIndex) || 1);
  const imageUrl = String(
    imageList?.[requestedPage - 1]?.image_url || ""
  ).trim();

  if (imageUrl && imageUrl !== failedImageUrl) {
    return {
      kind: "image",
      key: `image:${imageUrl}`,
      imageUrl,
    };
  }

  if (documentId) {
    return {
      kind: "pdf-api",
      key: `document:${documentId}`,
      apiPath: `/document/file/${documentId}`,
    };
  }

  if (pdfUrl) {
    const fileHint = `${fileName} ${String(pdfUrl).split("?")[0]}`;
    if (/\.(png|jpe?g|webp|gif)\b/i.test(fileHint)) {
      return pdfUrl !== failedImageUrl
        ? {
            kind: "image",
            key: `image:${pdfUrl}`,
            imageUrl: pdfUrl,
          }
        : { kind: "unavailable", key: "unavailable" };
    }
    return {
      kind: "pdf-url",
      key: `url:${pdfUrl}`,
      url: pdfUrl,
    };
  }

  return { kind: "unavailable", key: "unavailable" };
};

/**
 * Convert a pointer on a CSS-rotated image back into the original page-image
 * coordinate system used by OCR polygons.
 */
export const clientPointToPagePoint = ({
  clientX,
  clientY,
  rect,
  rotation = 0,
} = {}) => {
  if (
    !rect ||
    !Number.isFinite(clientX) ||
    !Number.isFinite(clientY) ||
    !Number.isFinite(rect.left) ||
    !Number.isFinite(rect.top) ||
    !(rect.width > 0) ||
    !(rect.height > 0)
  ) {
    return null;
  }

  const displayX = (clientX - rect.left) / rect.width;
  const displayY = (clientY - rect.top) / rect.height;
  if (
    displayX < 0 ||
    displayX > 1 ||
    displayY < 0 ||
    displayY > 1
  ) {
    return null;
  }

  switch (normalizeRotation(rotation)) {
    case 90:
      return { x: displayY, y: 1 - displayX };
    case 180:
      return { x: 1 - displayX, y: 1 - displayY };
    case 270:
      return { x: 1 - displayY, y: displayX };
    default:
      return { x: displayX, y: displayY };
  }
};
